import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Loader } from "lucide-react";
import useWindowSize from "../../../Hooks/useWindowSize";
import { callNonStoreApi } from "../../../NonStoreApis";
import {
  FetchInfluencerWiseStats,
  FetchStatsFromSpecificInfluencer,
} from "../../../Api/ApiEndPoints";
import { useParams } from "react-router";
import user from "../../../ImageAssets/user.jpg";
import { enqueueSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { AddFlex } from "../../../reusableStyles";

const ExpandableTable = ({ data, fetchNestedData }) => {
  const { isMobileView: isMobile } = useWindowSize();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loadingRows, setLoadingRows] = useState(new Set());
  const [nestedData, setNestedData] = useState({});

  useEffect(() => {}, []);

  const handleRowClick = async (rowId) => {
    if (expandedRows.has(rowId)) {
      const newExpandedRows = new Set(expandedRows);
      newExpandedRows.delete(rowId);
      setExpandedRows(newExpandedRows);
      return;
    }

    setExpandedRows(new Set(expandedRows).add(rowId));

    if (!nestedData[rowId]) {
      setLoadingRows(new Set(loadingRows).add(rowId));
      try {
        const nested = await fetchNestedData(rowId);
        setNestedData({ ...nestedData, [rowId]: nested });
      } catch (error) {
        console.error("Error fetching nested data:", error);
      } finally {
        const newLoadingRows = new Set(loadingRows);
        newLoadingRows.delete(rowId);
        setLoadingRows(newLoadingRows);
      }
    }
  };

  const renderInfluencerDetails = (influencer) => (
    <div className="flex items-center gap-3">
      <img
        src={influencer.photoUrl || user}
        alt={`${influencer.name}'s profile`}
        className="w-6 h-6 rounded-full object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-900">{influencer.name}</div>
        <div className="text-sm text-gray-500">@{influencer.username}</div>
      </div>
    </div>
  );

  // Mobile view
  if (isMobile()) {
    return (
      <div className="space-y-4">
        {data.map((row) => (
          <div
            key={row.id}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden"
          >
            <div
              className="p-4 cursor-pointer"
              onClick={() => handleRowClick(row.id)}
            >
              {renderInfluencerDetails(row)}
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Total Interactions</div>
                  <div className="font-medium">
                    {row.totalInteractions.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Phone Numbers</div>
                  <div className="font-medium">
                    {row.phoneNumbersCount.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            {expandedRows.has(row.id) && (
              <div className="border-t border-gray-200 bg-gray-50">
                {loadingRows.has(row.id) ? (
                  <div className="flex justify-center items-center py-6">
                    <Loader className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : nestedData[row.id] ? (
                  <div className="p-4 space-y-4">
                    {nestedData[row.id].map((nestedRow, index) => (
                      <div
                        key={`${row.id}-${index}`}
                        className="bg-white p-4 rounded-lg border border-gray-200"
                      >
                        <div className="font-medium text-gray-900">
                          {nestedRow.automationName}
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-gray-500">
                              Total Interactions
                            </div>
                            <div>
                              {nestedRow.totalInteractions.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-500">Phone Numbers</div>
                            <div>
                              {nestedRow.phoneNumbersCount.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Desktop view
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="w-8 py-3 px-4"></th>
            <th className="w-64 py-3 px-4 text-left text-sm font-medium text-gray-900">
              Influencer Details
            </th>
            <th className="py-3 px-4 text-center text-sm font-medium text-gray-900">
              Share
            </th>
            <th className="py-3 px-4 text-center text-sm font-medium text-gray-900">
              Comments
            </th>
            <th className="py-3 px-4 text-center text-sm font-medium text-gray-900">
              Story
            </th>
            <th className="py-3 px-4 text-center text-sm font-medium text-gray-900">
              Numbers
            </th>
            <th className="py-3 px-4 text-center text-sm font-medium text-gray-900">
              Emails
            </th>
            <th className="py-3 px-4 text-center text-sm font-medium text-gray-900">
              Followers
            </th>
            <th className="py-3 px-4 text-center text-sm font-medium text-gray-900">
              End
            </th>
            <th className="py-3 px-4 text-center text-sm font-medium text-gray-900">
              End Link Clicks
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((row) => (
            <React.Fragment key={row.id}>
              <tr
                onClick={() => handleRowClick(row.id)}
                className="cursor-pointer transition-colors"
              >
                <td className="py-3 px-4">
                  {expandedRows.has(row.id) ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </td>
                <td className="w-64 py-3 px-4">
                  {renderInfluencerDetails(row)}
                </td>
                {/* <td className="py-3 px-4 text-center text-gray-900">
                  {row.totalInteractions.toLocaleString()}
                </td> */}
                <td className="py-3 px-4 text-center text-gray-900">
                  {row.shareCount.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-center text-gray-900">
                  {row.commentsCount.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-center text-gray-900">
                  {row.storyInteractions.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-center text-gray-900">
                  {row.phoneNumbersCount.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-center text-gray-900">
                  {row.emailsCount.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-center text-gray-900">
                  {row.followersGained.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-center text-gray-900">
                  {row.end.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-center text-gray-900">
                  {row.endLinkClicks.toLocaleString()}
                </td>
              </tr>
              {expandedRows.has(row.id) &&
                (loadingRows.has(row.id) ? (
                  <tr>
                    <td colSpan={10}>
                      <div className="flex justify-center items-center py-8">
                        <Loader className="w-6 h-6 animate-spin text-blue-500" />
                      </div>
                    </td>
                  </tr>
                ) : nestedData[row.id] ? (
                  nestedData[row.id].map((nestedRow, index) => (
                    <tr className="bg-gray-100 border border-black">
                      <td className="py-3 px-4"></td>
                      <td className="w-64 py-3 px-4">
                        {nestedRow.automationName}
                      </td>
                      <td className="py-3 px-4 text-center text-black-900">
                        {nestedRow.shareCount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center text-black-900">
                        {nestedRow.commentsCount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center text-black-900">
                        {nestedRow.storyInteractions.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center text-black-900">
                        {nestedRow.phoneNumbersCount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center text-black-900">
                        {nestedRow.emailsCount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center text-balck-900">
                        {nestedRow.followersGained.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center text-black-900">
                        {nestedRow.end.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-900">
                        {nestedRow.endLinkClicks.toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : null)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Example usage with sample data
const CampaginInfluencerData = ({ apiEndPoint }) => {
  const { campaignId } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleGetData = async () => {
    try {
      setLoading(true);
      const response = await callNonStoreApi(apiEndPoint, {
        campaignId: campaignId,
      });
      const data = response.data.map((dataItem) => ({
        id: dataItem.instaUserId,
        name: dataItem.name || "Instagram User",
        username: dataItem.username,
        photoUrl: dataItem.profile_picture_url,
        shareCount: dataItem.stats.share || "-",
        commentsCount: dataItem.stats.comments || "-",
        storyInteractions: dataItem.stats.story || "-",
        totalInteractions:
          (dataItem.stats.comments || 0) +
          (dataItem.stats.share || 0) +
          (dataItem.stats.story || 0),
        phoneNumbersCount: dataItem.stats.phoneNumber || "-",
        emailsCount: dataItem.stats.email || "-",
        followersGained: dataItem.stats.followers || "-",
        end: dataItem.stats.end || "-",
        endLinkClicks: dataItem.stats.endLinkClicks || "-",
      }));
      setData(data);
    } catch (error) {
      enqueueSnackbar("something went wrong", {
        variant: "error",
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetData();
  }, [apiEndPoint]);

  const fetchNestedData = async (id) => {
    try {
      const response = await callNonStoreApi(FetchStatsFromSpecificInfluencer, {
        campaignId: campaignId,
        instaUserId: id,
      });
      const data = response.data.map((dataItem, index) => ({
        id: dataItem.instaUserId,
        automationName: dataItem.name || `Automation ${index + 1}`,
        shareCount: dataItem.stats.share || 0,
        commentsCount: dataItem.stats.comments || 0,
        storyInteractions: dataItem.stats.story || 0,
        totalInteractions:
          (dataItem.stats.comments || 0) +
          (dataItem.stats.share || 0) +
          (dataItem.stats.story || 0),
        phoneNumbersCount: dataItem.stats.phoneNumber || "-",
        emailsCount: dataItem.stats.email || "-",
        followersGained: dataItem.stats.followers || "-",
        end: dataItem.stats.end || "-",
        endLinkClicks: dataItem.stats.endLinkClicks || "-",
      }));
      return data;
    } catch (error) {
      enqueueSnackbar("something went wrong", {
        variant: "error",
      });
      return [];
    }
  };

  if (loading) {
    return (
      <AddFlex alignItems="center" justify="center" padding="50px">
        <CircularProgress />
      </AddFlex>
    );
  }

  return (
    <div className="bg-white">
      <ExpandableTable data={data} fetchNestedData={fetchNestedData} />
    </div>
  );
};

export { ExpandableTable };
export default CampaginInfluencerData;
